import React, { useEffect, useState, Component } from 'react';
import { UploadIcon, ArrowRightIcon, CheckIcon, XIcon, AlertTriangleIcon, ArrowLeftIcon, ArrowDownIcon, SaveIcon, EyeIcon, DatabaseIcon, ChevronRightIcon, ChevronLeftIcon, RefreshCwIcon, FilterIcon, SlidersIcon, DownloadIcon, PlusIcon, TrashIcon } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FileUploader from '../components/ui/FileUploader';
import Tabs from '../components/ui/Tabs';
import { useToast } from '../contexts/ToastContext';
import { parseCSV } from '../utils/csvParser';
import Papa from 'papaparse';
import { intentionsService } from '../services/intentionsService';
import { TeachingIntention } from '../types/intentions';
import { staffService } from '../services/staffService';
import { resolveIntendedFte, sameFte } from '../utils/intentionFte';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT } from '../types/schoolConfig';
// Field Mapping Component
const FieldMappingComponent = ({
  csvHeaders,
  dbFields,
  mappings,
  setMappings,
  transformations,
  setTransformations,
  openTransformModal,
  parsedCsvData
}) => {
  // Drag and drop functionality
  const [draggedHeader, setDraggedHeader] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  const handleDragStart = header => {
    setDraggedHeader(header);
  };
  const handleDragOver = (e, field) => {
    e.preventDefault();
    setDragTarget(field);
  };
  const handleDrop = (e, dbField) => {
    e.preventDefault();
    if (draggedHeader && dbField) {
      setMappings({
        ...mappings,
        [draggedHeader]: dbField
      });
      setDraggedHeader(null);
      setDragTarget(null);
    }
  };
  // Auto-map similar fields
  const autoMapFields = () => {
    const newMappings = {
      ...mappings
    };
    csvHeaders.forEach(header => {
      // Skip already mapped fields
      if (newMappings[header] && newMappings[header] !== '') return;
      // Try to find a matching db field
      const normalizedHeader = header.toLowerCase().replace(/[_\s]/g, '');
      const matchingField = dbFields.find(field => {
        const normalizedField = field.name.toLowerCase().replace(/[_\s]/g, '');
        return normalizedField === normalizedHeader || normalizedField.includes(normalizedHeader) || normalizedHeader.includes(normalizedField);
      });
      if (matchingField) {
        newMappings[header] = matchingField.name;
      }
    });
    setMappings(newMappings);
  };
  // Clear all mappings
  const clearAllMappings = () => {
    const newMappings = {};
    csvHeaders.forEach(header => {
      newMappings[header] = '';
    });
    setMappings(newMappings);
  };
  // Get validation status for a mapping
  const getMappingValidationStatus = (csvHeader, dbField) => {
    if (dbField === 'ignore') return 'valid';
    if (dbField === '') return 'unmapped';
    const field = dbFields.find(f => f.name === dbField);
    if (!field) return 'invalid';
    // Check data type compatibility
    const sampleValue = parsedCsvData[0]?.[csvHeader];
    if (field.type === 'number' && isNaN(Number(sampleValue))) {
      return 'warning';
    }
    if (field.type === 'date' && isNaN(Date.parse(sampleValue))) {
      return 'warning';
    }
    if (field.type === 'email' && !sampleValue?.includes('@')) {
      return 'warning';
    }
    return 'valid';
  };
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-ink-900">
          Map CSV Fields to Database Fields
        </h3>
        <div className="flex space-x-2">
          <Button variant="secondary" size="sm" leftIcon={<RefreshCwIcon className="h-4 w-4" />} onClick={autoMapFields}>
            Auto-Map Fields
          </Button>
          <Button variant="secondary" size="sm" leftIcon={<TrashIcon className="h-4 w-4" />} onClick={clearAllMappings}>
            Clear All
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* CSV Columns Panel */}
        <Card className="lg:col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-sm font-medium text-ink-700">CSV Columns</h4>
            <Button variant="secondary" size="sm" leftIcon={<FilterIcon className="h-4 w-4" />}>
              Filter
            </Button>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
            {csvHeaders.map(header => {
            const mappedTo = mappings[header] || '';
            const validationStatus = getMappingValidationStatus(header, mappedTo);
            return <div key={header} className={`p-3 border rounded-md ${validationStatus === 'valid' ? 'border-ok-border bg-ok-bg' : validationStatus === 'warning' ? 'border-warn-border bg-warn-bg' : validationStatus === 'unmapped' ? 'border-ink-200 bg-ink-50' : 'border-danger-border bg-danger-bg'} cursor-grab`} draggable onDragStart={() => handleDragStart(header)}>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-ink-900 flex items-center">
                        <span className="mr-2">{header}</span>
                        {mappedTo && <span className="text-xs text-ink-500">
                            → {mappedTo.replace(/_/g, ' ')}
                          </span>}
                      </div>
                      <div className="text-xs text-ink-500 mt-1">
                        <div className="flex items-center">
                          <span className="mr-2">Sample:</span>
                          <span className="font-mono bg-ink-100 px-1 py-0.5 rounded text-xs">
                            {parsedCsvData[0]?.[header]}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {transformations[header] && <span className="px-2 py-1 text-xs rounded-full bg-navy-100 text-navy-800">
                          Transformed
                        </span>}
                      <button className="text-navy-600 hover:text-navy-800 text-sm" onClick={() => openTransformModal(header)}>
                        Transform
                      </button>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </Card>
        {/* Mapping Arrows */}
        <div className="hidden lg:flex flex-col items-center justify-center">
          <div className="w-full flex-1 flex flex-col items-center justify-center space-y-4">
            <div className="text-center text-sm text-ink-500">
              Drag columns from the left panel and drop them onto the matching
              database fields on the right
            </div>
            <div className="flex flex-col items-center space-y-2">
              <ArrowRightIcon className="h-8 w-8 text-navy-500" />
              <div className="text-xs text-ink-500">Drag & Drop</div>
            </div>
            <div className="text-center text-sm text-ink-500 mt-4">
              Or use the dropdown selectors in each field
            </div>
          </div>
        </div>
        {/* Database Fields Panel */}
        <Card className="lg:col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-sm font-medium text-ink-700">
              Database Fields
            </h4>
            <div className="text-xs px-2 py-1 bg-navy-100 text-navy-800 rounded-full">
              Required Fields: {dbFields.filter(f => f.required).length}
            </div>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
            {dbFields.map(field => {
            const mappedFromColumns = Object.entries(mappings).filter(([_, dbField]) => dbField === field.name);
            const isMapped = mappedFromColumns.length > 0;
            const validationStatus = isMapped ? 'valid' : field.required ? 'warning' : 'unmapped';
            return <div key={field.name} className={`p-3 border rounded-md ${validationStatus === 'valid' ? 'border-ok-border bg-ok-bg' : validationStatus === 'warning' ? 'border-warn-border bg-warn-bg' : validationStatus === 'unmapped' ? 'border-ink-200 bg-ink-50' : 'border-danger-border bg-danger-bg'}`} onDragOver={e => handleDragOver(e, field.name)} onDrop={e => handleDrop(e, field.name)}>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-ink-900 flex items-center">
                        <span className="mr-2">
                          {field.name.replace(/_/g, ' ')}
                        </span>
                        {field.required && <span className="text-xs text-danger-fg font-medium">
                            Required
                          </span>}
                      </div>
                      <div className="text-xs text-ink-500 mt-1">
                        {field.description}
                      </div>
                      <div className="text-xs text-ink-500 mt-1">
                        <span className="px-1.5 py-0.5 bg-ink-200 rounded text-xs">
                          Type: {field.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2">
                      {isMapped && (
                        <div className="flex flex-col space-y-1">
                          {mappedFromColumns.map(([csvColumn, _], index) => (
                            <div key={csvColumn} className="flex items-center text-xs">
                              <span className="text-ink-500 mr-2">
                                ← {csvColumn}
                              </span>
                              <button className="text-danger-fg hover:text-danger-fg" onClick={() => {
                                const newMappings = { ...mappings };
                                newMappings[csvColumn] = '';
                                setMappings(newMappings);
                              }}>
                                <XIcon className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                          {mappedFromColumns.length > 1 && (
                            <div className="text-xs text-navy-600 font-medium">
                              {mappedFromColumns.length} columns mapped
                            </div>
                          )}
                        </div>
                      )}
                      {/* Always show dropdown to allow additional mappings */}
                      <select className="text-sm border-ink-300 rounded-md" value="" onChange={e => {
                        if (e.target.value) {
                          const newMappings = { ...mappings };
                          newMappings[e.target.value] = field.name;
                          setMappings(newMappings);
                        }
                      }}>
                        <option value="">{isMapped ? 'Add another mapping...' : 'Map from CSV...'}</option>
                        {csvHeaders.map(header => <option key={header} value={header} disabled={mappings[header] && mappings[header] !== '' && mappings[header] !== field.name}>
                            {header} {mappings[header] && mappings[header] === field.name ? '(already mapped here)' : mappings[header] && mappings[header] !== '' ? `(mapped to ${mappings[header]})` : ''}
                          </option>)}
                      </select>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </Card>
      </div>
    </div>;
};
const ImportData: React.FC = () => {
  const {
    addToast
  } = useToast();
  const [activeStep, setActiveStep] = useState(1);
  const [files, setFiles] = useState<File[]>([]);
  const [importType, setImportType] = useState<string>('staff');
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [validationResults, setValidationResults] = useState<any>(null);
  const [parsedCsvData, setParsedCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);
  const [csvParsing, setCsvParsing] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<string | null>(null);
  const [savedMappings, setSavedMappings] = useState<any[]>([{
    id: '1',
    name: 'Staff Import - Default',
    type: 'staff',
    isTemplate: true
  }, {
    id: '2',
    name: 'Subjects Import - Default',
    type: 'subjects',
    isTemplate: true
  }, {
    id: '3',
    name: 'Classes Import - Default',
    type: 'classes',
    isTemplate: true
  }]);
  const [mappingName, setMappingName] = useState('');
  const [showSaveMappingModal, setShowSaveMappingModal] = useState(false);
  const [transformations, setTransformations] = useState<Record<string, any>>({});
  const [showTransformModal, setShowTransformModal] = useState(false);
  const [currentTransformField, setCurrentTransformField] = useState<string | null>(null);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  // Sample database fields based on import type
  const getDbFields = () => {
    switch (importType) {
      case 'staff':
        return [{
          name: 'staff_name',
          required: true,
          type: 'string',
          description: 'Full name of staff member'
        }, {
          name: 'department',
          required: true,
          type: 'string',
          description: 'Department name'
        }, {
          name: 'subjects',
          required: false,
          type: 'array',
          description: 'Comma-separated list of qualified subjects'
        }, {
          name: 'max_periods',
          required: false,
          type: 'number',
          description: 'Maximum teaching periods per week'
        }, {
          name: 'roles',
          required: false,
          type: 'array',
          description: 'Additional roles within the school'
        }, {
          name: 'start_date',
          required: false,
          type: 'date',
          description: 'Employment start date (YYYY-MM-DD)'
        }, {
          name: 'status',
          required: false,
          type: 'string',
          description: 'Current status (active, on leave, etc)'
        }];
      case 'subjects':
        return [{
          name: 'subject_name',
          required: true,
          type: 'string',
          description: 'Name of the subject'
        }, {
          name: 'subject_code',
          required: true,
          type: 'string',
          description: 'Unique subject code'
        }, {
          name: 'department',
          required: true,
          type: 'string',
          description: 'Department name'
        }, {
          name: 'year_levels',
          required: true,
          type: 'array',
          description: 'Comma-separated list of applicable year levels'
        }, {
          name: 'periods',
          required: true,
          type: 'number',
          description: 'Number of periods per week'
        }, {
          name: 'requires_specialist',
          required: false,
          type: 'boolean',
          description: 'Whether specialist teacher is required (true/false)'
        }, {
          name: 'max_class_size',
          required: false,
          type: 'number',
          description: 'Maximum number of students per class'
        }];
      case 'classes':
        return [{
          name: 'class_name',
          required: true,
          type: 'string',
          description: 'Name of the class'
        }, {
          name: 'subject_id',
          required: true,
          type: 'string',
          description: 'Subject ID or code'
        }, {
          name: 'year_level',
          required: true,
          type: 'number',
          description: 'Year level (7-12)'
        }, {
          name: 'line',
          required: true,
          type: 'string',
          description: 'Timetable line'
        }, {
          name: 'room',
          required: false,
          type: 'string',
          description: 'Assigned room'
        }, {
          name: 'student_count',
          required: false,
          type: 'number',
          description: 'Number of students'
        }, {
          name: 'status',
          required: false,
          type: 'string',
          description: 'Class status'
        }];
      case 'teaching-intentions':
        return [{
          name: 'staff_name',
          required: true,
          type: 'string',
          description: 'Full name of staff member'
        }, {
          name: 'employment_type',
          required: true,
          type: 'string',
          description: 'Current employment type (ongoing/fixed-term)'
        }, {
          name: 'intention_to_return',
          required: true,
          type: 'string',
          description: 'Intention to return (yes/no/uncertain)'
        }, {
          name: 'target_fte',
          required: false,
          type: 'number',
          description: 'Desired FTE for next year (0.0-1.0)'
        }, {
          name: 'preferred_days',
          required: false,
          type: 'string',
          description: 'Preferred working days'
        }, {
          name: 'pol_position',
          required: false,
          type: 'string',
          description: 'Position of Leadership role'
        }, {
          name: 'pol_minutes',
          required: false,
          type: 'number',
          description: 'Minutes allocated for POL position'
        }, {
          name: 'other_allowances',
          required: false,
          type: 'string',
          description: 'Other allowances or roles'
        }, {
          name: 'department',
          required: true,
          type: 'string',
          description: 'Primary faculty/department'
        }, {
          name: 'teaching_preference_1',
          required: false,
          type: 'string',
          description: 'First teaching preference'
        }, {
          name: 'teaching_preference_2',
          required: false,
          type: 'string',
          description: 'Second teaching preference'
        }, {
          name: 'teaching_preference_3',
          required: false,
          type: 'string',
          description: 'Third teaching preference'
        }, {
          name: 'teaching_preference_4',
          required: false,
          type: 'string',
          description: 'Fourth teaching preference'
        }, {
          name: 'teaching_preference_5',
          required: false,
          type: 'string',
          description: 'Fifth teaching preference'
        }, {
          name: 'teaching_preference_6',
          required: false,
          type: 'string',
          description: 'Sixth teaching preference'
        }, {
          name: 'ruah_preference_1',
          required: false,
          type: 'string',
          description: 'First RUAH preference'
        }, {
          name: 'ruah_preference_2',
          required: false,
          type: 'string',
          description: 'Second RUAH preference'
        }, {
          name: 'additional_comments',
          required: false,
          type: 'string',
          description: 'Additional comments or requests'
        }];
      default:
        return [];
    }
  };
  // Enhanced CSV parser that tries multiple delimiters
  const parseCSVWithMultipleDelimiters = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      // Try different delimiters in order of likelihood
      const delimitersToTry = [',', ';', '\t', '|'];
      let bestResult: any = null;
      let maxColumns = 0;
      let attemptCount = 0;

      const tryNextDelimiter = (delimiterIndex: number) => {
        if (delimiterIndex >= delimitersToTry.length) {
          // If no delimiter worked well, use the best result we found
          if (bestResult) {
            console.log('🔧 CSV Parse Result: Using best delimiter result with', maxColumns, 'columns');
            resolve(bestResult.data);
          } else {
            reject(new Error('Could not parse CSV with any common delimiter'));
          }
          return;
        }

        const delimiter = delimitersToTry[delimiterIndex];
        console.log('🔍 Trying delimiter:', delimiter === ',' ? 'comma' : delimiter === ';' ? 'semicolon' : delimiter === '\t' ? 'tab' : 'pipe');

        Papa.parse(file, {
          header: true,
          delimiter: delimiter,
          skipEmptyLines: true,
          dynamicTyping: false,
          complete: (result) => {
            attemptCount++;
            
            if (result.data && result.data.length > 0) {
              const firstRow = result.data[0] as any;
              const columnCount = Object.keys(firstRow).length;
              
              console.log(`📊 Delimiter "${delimiter}" result:`, {
                columns: columnCount,
                firstRowKeys: Object.keys(firstRow),
                sampleData: firstRow
              });

              // A good result should have multiple columns and meaningful data
              if (columnCount > maxColumns) {
                maxColumns = columnCount;
                bestResult = result;
                console.log('✅ New best result with', columnCount, 'columns');
              }
            }

            // Try next delimiter
            tryNextDelimiter(delimiterIndex + 1);
          },
          error: (error) => {
            console.log('❌ Error with delimiter', delimiter, ':', error);
            tryNextDelimiter(delimiterIndex + 1);
          }
        });
      };

      // Start trying delimiters
      tryNextDelimiter(0);
    });
  };

  // Detect headers and filter out header rows from CSV data
  const detectHeadersAndData = (rawData: any[]) => {
    if (rawData.length === 0) {
      return { headers: [], dataRows: [] };
    }

    // Find the first row that looks like actual data (contains a name in the first column)
    let dataStartIndex = 0;
    let headers: string[] = [];

    // Look for patterns that indicate header rows vs data rows
    for (let i = 0; i < rawData.length; i++) {
      const row = rawData[i];
      const firstColumn = Object.values(row)[0] as string;
      
      // Skip if first column is empty, contains question text, or other header indicators
      if (!firstColumn || 
          firstColumn.includes('Are you currently employed') ||
          firstColumn.includes('Please note') ||
          firstColumn.includes('PLEASE TYPE') ||
          firstColumn.length > 100 || // Likely instruction text
          firstColumn.toLowerCase().includes('question') ||
          firstColumn.toLowerCase().includes('instruction')) {
        continue;
      }
      
      // Check if this looks like a person's name (2+ parts, reasonable length)
      const nameParts = firstColumn.trim().split(/\s+/);
      if (nameParts.length >= 2 && 
          firstColumn.length <= 50 && 
          /^[A-Za-z\s'.-]+$/.test(firstColumn)) {
        dataStartIndex = i;
        break;
      }
    }

    // Always use the column structure from the first row to maintain all columns
    // The CSV parser should have properly structured all rows with the same keys
    headers = Object.keys(rawData[0]);
    
    // Return data rows starting from detected index
    const dataRows = rawData.slice(dataStartIndex);

    // Debug logging
    console.log('🔍 Header Detection Debug:', {
      totalRows: rawData.length,
      dataStartIndex,
      headerCount: headers.length,
      headers: headers,
      firstDataRow: dataRows[0],
      skippedRows: rawData.length - dataRows.length
    });

    return { headers, dataRows };
  };

  // Helper function to get value from multiple possible CSV columns that map to the same database field
  const getValueFromMultipleMappings = (row: any, mappings: Record<string, string>, targetField: string): string => {
    const sourceColumns = Object.keys(mappings).filter(key => mappings[key] === targetField);
    
    for (const column of sourceColumns) {
      const value = row[column];
      if (value && value.trim()) {
        return value.trim();
      }
    }
    
    return '';
  };

  // Transform CSV data to Teaching Intentions format
  const transformToTeachingIntentions = (csvData: any[], mappings: Record<string, string>, fileName: string): TeachingIntention[] => {
    return csvData.map((row, index) => {
      // Generate a unique ID for this intention
      const id = `ti_${Date.now()}_${index}`;
      
      // Extract staff name and generate staff ID (3-letter code)
      const staffName = row[Object.keys(mappings).find(key => mappings[key] === 'staff_name') || ''] || '';
      const nameParts = staffName.split(' ');
      const staffId = nameParts.length >= 2 
        ? (nameParts[0].substring(0, 1) + nameParts[nameParts.length - 1].substring(0, 2)).toUpperCase()
        : staffName.substring(0, 3).toUpperCase();

      // Extract email if provided (optional for intentions import)
      const email = row[Object.keys(mappings).find(key => mappings[key] === 'staff_email') || ''] || undefined;

      // Transform employment type
      const rawEmploymentType = row[Object.keys(mappings).find(key => mappings[key] === 'employment_type') || ''] || '';
      const currentEmploymentType = rawEmploymentType.toLowerCase().includes('ongoing') ? 'ongoing' : 
                                   rawEmploymentType.toLowerCase().includes('fixed') ? 'fixed-term' : 'ongoing';

      // Transform intention to return - handle multiple columns mapping to same field
      const rawIntention = getValueFromMultipleMappings(row, mappings, 'intention_to_return');
      const intentionToReturn = rawIntention.toLowerCase().includes('yes') ? 'yes' :
                               rawIntention.toLowerCase().includes('no') ? 'no' : 'uncertain';

      // Parse FTE
      const targetFTEStr = row[Object.keys(mappings).find(key => mappings[key] === 'target_fte') || ''] || '1';
      const targetFTE = Math.min(1.0, Math.max(0.0, parseFloat(targetFTEStr) || 1.0));
      
      // For currentFTE, use targetFTE as default since CSV doesn't have separate current FTE
      const currentFTE = targetFTE;
      
      // Determine desired employment status based on target FTE and employment type
      const desiredEmploymentStatus = targetFTE >= 1.0 ? 'full-time' : 
                                    targetFTE >= 0.5 ? 'part-time' : 'casual';

      // Get department
      const department = row[Object.keys(mappings).find(key => mappings[key] === 'department') || ''] || '';
      
      // Get preferred days
      const preferredDays = row[Object.keys(mappings).find(key => mappings[key] === 'preferred_days') || ''] || undefined;
      
      // Get additional comments
      const additionalComments = row[Object.keys(mappings).find(key => mappings[key] === 'additional_comments') || ''] || undefined;
      
      // Get POL position and other allowances
      const polPosition = row[Object.keys(mappings).find(key => mappings[key] === 'pol_position') || ''] || undefined;
      const otherAllowances = row[Object.keys(mappings).find(key => mappings[key] === 'other_allowances') || ''] || undefined;

      // Build teaching preferences array
      const teachingPreferences = [];
      for (let i = 1; i <= 6; i++) {
        const prefKey = Object.keys(mappings).find(key => mappings[key] === `teaching_preference_${i}`);
        const preference = prefKey ? row[prefKey] : '';
        if (preference && preference.trim()) {
          teachingPreferences.push({
            rank: i,
            learningArea: department, // Use department as learning area
            specificSubject: preference.trim()
          });
        }
      }

      // Build RUAH preferences array
      const ruahPreferences = [];
      for (let i = 1; i <= 2; i++) {
        const ruahKey = Object.keys(mappings).find(key => mappings[key] === `ruah_preference_${i}`);
        const ruahPref = ruahKey ? row[ruahKey] : '';
        if (ruahPref && ruahPref.trim()) {
          ruahPreferences.push(ruahPref.trim());
        }
      }

      // Parse POL minutes
      const polMinutesStr = row[Object.keys(mappings).find(key => mappings[key] === 'pol_minutes') || ''] || '';
      const polMinutes = polMinutesStr ? parseInt(polMinutesStr) : undefined;

      // Calculate max teaching minutes based on target FTE
      const maxTeachingMinutes = Math.round(targetFTE * DEFAULT_BASE_MINUTES_PER_FORTNIGHT);

      // Create the teaching intention object
      const intention: TeachingIntention = {
        id,
        staffId,
        staffName,
        email, // Optional email field (can be undefined)
        department,
        year: 2026, // Hardcoded for this import
        currentEmploymentType: currentEmploymentType as 'ongoing' | 'fixed-term' | 'casual' | 'full-time' | 'part-time',
        currentFTE, // Use calculated currentFTE
        intentionToReturn: intentionToReturn as 'yes' | 'no' | 'uncertain',
        desiredEmploymentStatus: desiredEmploymentStatus as 'full-time' | 'part-time' | 'casual' | 'not-returning',
        targetFTE,
        preferredDays,
        teachingPreferences,
        ruahPreferences: ruahPreferences.length > 0 ? ruahPreferences : undefined,
        comfortableTeachingAreas: teachingPreferences.map(tp => tp.specificSubject).filter(Boolean), // Derive from preferences
        leadingLearningRole: undefined, // Not in CSV, could be added later
        additionalResponsibilities: undefined, // Not in CSV, could be added later  
        polPosition,
        polMinutes,
        otherAllowances,
        additionalComments,
        submittedAt: new Date(),
        lastUpdatedAt: new Date(),
        status: 'imported',
        importSource: fileName,
        maxTeachingMinutes,
        currentLoad: undefined, // Will be calculated from grid assignments later
        satisfactionScore: undefined // Will be calculated after allocation
      };

      return intention;
    });
  };

  // Get mapped data preview
  const getMappedDataPreview = () => {
    return parsedCsvData.slice(0, 3).map(row => {
      const mappedRow: Record<string, any> = {};
      
      // Get unique database fields that have mappings
      const uniqueDbFields = Array.from(new Set(Object.values(mappings).filter(dbField => dbField && dbField !== 'ignore')));
      
      // For each unique database field, get the value using multiple mappings helper
      uniqueDbFields.forEach(dbField => {
        let value = getValueFromMultipleMappings(row, mappings, dbField);
        
        // Apply transformations if any (check all CSV headers that map to this db field)
        const csvHeadersForField = Object.entries(mappings)
          .filter(([, mappedDbField]) => mappedDbField === dbField)
          .map(([csvHeader]) => csvHeader);
          
        for (const csvHeader of csvHeadersForField) {
          if (transformations[csvHeader] && value) {
            const transform = transformations[csvHeader];
            // Apply trim
            if (transform.trim && typeof value === 'string') {
              value = value.trim();
            }
            // Apply default value if empty
            if (transform.defaultValue && (!value || value === '')) {
              value = transform.defaultValue;
            }
            // Apply split for array types
            if (transform.splitOn && typeof value === 'string') {
              value = value.split(transform.splitOn).map(item => item.trim());
            }
            // Apply number conversion
            if (transform.convertToNumber && !isNaN(Number(value))) {
              value = Number(value);
            }
            // Apply boolean conversion
            if (transform.convertToBoolean) {
              value = ['true', 'yes', '1', 'y'].includes(String(value).toLowerCase());
            }
            break; // Apply transformation from first matching CSV header only
          }
        }
        mappedRow[dbField] = value;
      });
      return mappedRow;
    });
  };
  // Handle file upload
  const handleFilesSelected = async (selectedFiles: File[]) => {
    console.log('🚀 File Upload Started:', {
      fileCount: selectedFiles.length,
      fileName: selectedFiles[0]?.name,
      fileSize: selectedFiles[0]?.size,
      fileType: selectedFiles[0]?.type
    });
    
    setFiles(selectedFiles);
    // Reset later steps if files change
    setValidationResults(null);
    setCsvParseError(null);
    if (activeStep > 1) {
      setActiveStep(1);
    }
    
    if (selectedFiles.length > 0) {
      const file = selectedFiles[0]; // Handle first file for now
      console.log('📁 Processing file:', file.name, 'Size:', file.size, 'bytes');
      setCsvParsing(true);
      
      try {
        // Parse the actual CSV file with multiple delimiter attempts
        console.log('🚀 Starting multi-delimiter CSV parsing...');
        const rawCsvData = await parseCSVWithMultipleDelimiters(file);
        
        console.log('🔍 Raw CSV Debug:', {
          totalRows: rawCsvData.length,
          firstRow: rawCsvData[0],
          secondRow: rawCsvData[1],
          thirdRow: rawCsvData[2],
          firstRowKeys: Object.keys(rawCsvData[0] || {}),
          firstRowValues: Object.values(rawCsvData[0] || {})
        });
        
        if (rawCsvData.length === 0) {
          throw new Error('CSV file appears to be empty');
        }
        
        // Filter out header rows and find actual data
        const { headers, dataRows } = detectHeadersAndData(rawCsvData);
        
        if (dataRows.length === 0) {
          throw new Error('No data rows found after headers');
        }
        
        setCsvHeaders(headers);
        setParsedCsvData(dataRows);
        
        // Auto-suggest mappings based on header names
        const suggestedMappings: Record<string, string> = {};
        headers.forEach(header => {
          // Try to find a matching db field
          const dbFields = getDbFields();
          const matchingField = dbFields.find(field => 
            field.name.toLowerCase().replace(/_/g, ' ') === header.toLowerCase() || 
            field.name.toLowerCase().replace(/_/g, '') === header.toLowerCase().replace(/\s/g, '')
          );
          if (matchingField) {
            suggestedMappings[header] = matchingField.name;
          }
        });
        setMappings(suggestedMappings);
        
        addToast(`Successfully parsed CSV file with ${dataRows.length} data rows (${rawCsvData.length - dataRows.length} header rows skipped)`, 'success');
      } catch (error) {
        console.error('CSV parsing error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error parsing CSV file';
        setCsvParseError(errorMessage);
        addToast(`Failed to parse CSV: ${errorMessage}`, 'error');
        
        // Clear data on error
        setCsvHeaders([]);
        setParsedCsvData([]);
        setMappings({});
      } finally {
        setCsvParsing(false);
      }
    }
  };
  // Handle mapping change
  const handleMappingChange = (csvHeader: string, dbField: string) => {
    setMappings({
      ...mappings,
      [csvHeader]: dbField
    });
  };
  // Check if a mapping is valid
  const isMappingValid = (csvHeader: string, dbField: string) => {
    const field = getDbFields().find(f => f.name === dbField);
    if (!field) return true; // Ignore field is always valid
    // Check if the field is required and mapped
    if (field.required) {
      return dbField !== '';
    }
    return true;
  };
  // Get validation status for a mapping
  const getMappingValidationStatus = (csvHeader: string, dbField: string) => {
    if (dbField === 'ignore') return 'valid';
    if (dbField === '') return 'unmapped';
    const field = getDbFields().find(f => f.name === dbField);
    if (!field) return 'invalid';
    // Check data type compatibility
    const sampleValue = parsedCsvData[0]?.[csvHeader];
    if (field.type === 'number' && isNaN(Number(sampleValue))) {
      return 'warning';
    }
    if (field.type === 'date' && isNaN(Date.parse(sampleValue))) {
      return 'warning';
    }
    if (field.type === 'email' && !sampleValue?.includes('@')) {
      return 'warning';
    }
    return 'valid';
  };
  // Get all required fields
  const getRequiredFields = () => {
    return getDbFields().filter(field => field.required).map(field => field.name);
  };
  // Check if all required fields are mapped
  const areRequiredFieldsMapped = () => {
    const requiredFields = getRequiredFields();
    const mappedFields = Object.values(mappings);
    return requiredFields.every(field => mappedFields.includes(field));
  };
  // Load a saved mapping
  const loadSavedMapping = (mappingId: string) => {
    // In a real app, we would fetch this from the database
    // For now, let's simulate it
    addToast('Mapping template loaded', 'success');
    // Simulate loading a mapping based on the current import type
    if (importType === 'staff') {
      setMappings({
        Name: 'staff_name',
        Email: 'email',
        Department: 'department',
        Subjects: 'subjects',
        'Max Periods': 'max_periods',
        Phone: 'ignore',
        'Start Date': 'start_date'
      });
      setTransformations({
        Subjects: {
          splitOn: ',',
          trim: true
        },
        'Max Periods': {
          convertToNumber: true,
          defaultValue: '20'
        },
        'Start Date': {
          trim: true
        }
      });
    } else if (importType === 'teaching-intentions') {
      // Simulate teaching intentions mapping for real CSV headers
      setMappings({
        'Please type in your name here\n': 'staff_name',
        'Are you currently employed on an Ongoing or a Fixed Term basis? ': 'employment_type',
        'For Ongoing Staff - Is it your intention to keep teaching next year?': 'intention_to_return',
        'Target FTE': 'target_fte',
        'For Part time staff, state your preferred days (we will do our best to meet your preference)': 'preferred_days',
        'POL position': 'pol_position',
        'POL Minutes': 'pol_minutes',
        'Other Allowance': 'other_allowances',
        'Faculty': 'department',
        'First Teaching Preference ': 'teaching_preference_1',
        'Second Preference': 'teaching_preference_2',
        'Third Preference': 'teaching_preference_3',
        'Fourth Preferene': 'teaching_preference_4',
        'Fifth Preference': 'teaching_preference_5',
        'Sixth Preference': 'teaching_preference_6',
        'First RUAH Preference': 'ruah_preference_1',
        'Second RUAH Preference': 'ruah_preference_2',
        'Additional Comments (optional)\nPlease note - Whilst every effort will be made to consider teachers\' preferences and requests for the 2026 school year, there can be no guarantee that all of these will ': 'additional_comments'
      });
    } else if (importType === 'subjects') {
      // Simulate subject mapping
      setMappings({
        Name: 'subject_name',
        Code: 'subject_code',
        Department: 'department',
        'Year Levels': 'year_levels',
        Periods: 'periods',
        Specialist: 'requires_specialist',
        'Class Size': 'max_class_size'
      });
    }
    setSelectedMapping(mappingId);
  };
  // Save current mapping (kept for this session only — not yet persisted).
  const saveCurrentMapping = () => {
    addToast(`Mapping "${mappingName}" saved for this session`, 'success');
    setSavedMappings([...savedMappings, {
      id: Date.now().toString(),
      name: mappingName,
      type: importType,
      isTemplate: false
    }]);
    setShowSaveMappingModal(false);
  };
  // Open transformation modal for a field
  const openTransformModal = (csvHeader: string) => {
    setCurrentTransformField(csvHeader);
    setShowTransformModal(true);
  };
  // Save transformation
  const saveTransformation = (transformation: any) => {
    if (!currentTransformField) return;
    setTransformations({
      ...transformations,
      [currentTransformField]: transformation
    });
    setShowTransformModal(false);
    setCurrentTransformField(null);
    addToast(`Transformation for "${currentTransformField}" applied`, 'success');
  };
  // Validate CSV data against mappings
  const validateCsvData = () => {
    const issues: any[] = [];
    let validRows = 0;
    let warningCount = 0;
    let errorCount = 0;
    
    // Get required fields based on import type
    const requiredFields = getRequiredFields();
    const mappedFields = Object.values(mappings).filter(field => field && field !== 'ignore');
    
    // Check if required fields are mapped
    const missingRequiredFields = requiredFields.filter(field => !mappedFields.includes(field));
    if (missingRequiredFields.length > 0) {
      issues.push({
        row: 'Header',
        type: 'error',
        message: `Missing required field mappings: ${missingRequiredFields.join(', ')}`,
        field: 'mapping',
        value: '',
        suggestion: 'Map all required fields before validation'
      });
      errorCount++;
    }
    
    // Validate each data row
    parsedCsvData.forEach((row, index) => {
      let rowHasError = false;
      let rowHasWarning = false;
      
      // Check required fields have values (treat as warnings, not errors)
      requiredFields.forEach(dbField => {
        const value = getValueFromMultipleMappings(row, mappings, dbField);
        if (!value || value.toString().trim() === '') {
          issues.push({
            row: index + 1,
            type: 'warning',
            message: `Missing data for: ${dbField.replace(/_/g, ' ')}`,
            field: dbField,
            value: value || '',
            suggestion: `Will be imported with empty value - add manually later`
          });
          warningCount++;
          rowHasWarning = true;
        }
      });
      
      // Validate email format if present (for staff import)
      if (importType === 'staff' && mappings['email']) {
        const email = getValueFromMultipleMappings(row, mappings, 'email');
        if (email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          issues.push({
            row: index + 1,
            type: 'error',
            message: 'Invalid email format',
            field: 'email',
            value: email,
            suggestion: 'Correct the email format (example@domain.com)'
          });
          errorCount++;
          rowHasError = true;
        }
      }
      
      // Check for empty staff names (treat as warning)
      const staffName = getValueFromMultipleMappings(row, mappings, 'staff_name');
      if (!staffName || staffName.toString().trim() === '') {
        issues.push({
          row: index + 1,
          type: 'warning',
          message: 'Missing staff name',
          field: 'staff_name',
          value: staffName || '',
          suggestion: 'Will create record with empty name - add manually later'
        });
        warningCount++;
        rowHasWarning = true;
      }
      
      // Validate FTE values for teaching intentions
      if (importType === 'teaching-intentions') {
        const targetFTE = getValueFromMultipleMappings(row, mappings, 'target_fte');
        if (targetFTE && (isNaN(Number(targetFTE)) || Number(targetFTE) < 0 || Number(targetFTE) > 1)) {
          issues.push({
            row: index + 1,
            type: 'warning',
            message: 'Target FTE should be between 0.0 and 1.0',
            field: 'target_fte',
            value: targetFTE,
            suggestion: 'Use decimal format (e.g., 0.8 for 80%)'
          });
          warningCount++;
          rowHasWarning = true;
        }
      }
      
      // Count valid rows (no errors)
      if (!rowHasError) {
        validRows++;
      }
    });
    
    return {
      totalRows: parsedCsvData.length,
      validRows,
      warnings: warningCount,
      errors: errorCount,
      details: issues
    };
  };

  // Move to next step
  const nextStep = () => {
    if (activeStep === 2) {
      // Real validation
      setValidationInProgress(true);
      setTimeout(() => {
        const results = validateCsvData();
        setValidationResults(results);
        setValidationInProgress(false);
        setActiveStep(3);
      }, 1000);
    } else {
      setActiveStep(activeStep + 1);
    }
  };
  // Previous step
  const prevStep = () => {
    setActiveStep(activeStep - 1);
  };
  // Import data
  const importData = async () => {
    setImportInProgress(true);
    
    try {
      if (importType === 'teaching-intentions') {
        // Transform data to Teaching Intentions format
        const fileName = files[0]?.name || 'Unknown';
        const teachingIntentions = transformToTeachingIntentions(parsedCsvData, mappings, fileName);
        
        console.log('📊 Transformed Teaching Intentions:', {
          count: teachingIntentions.length,
          sample: teachingIntentions[0]
        });

        // Save each teaching intention to the database and create staff records
        let processed = 0;
        let staffCreated = 0;
        let staffWithoutEmail = 0;
        let staffFteNeedsReview = 0;
        
        for (const intention of teachingIntentions) {
          try {
            // First, try to create/update the staff record (without requiring email)
            try {
              const staffData = {
                staff_id: intention.staffId,
                name: intention.staffName,
                email: intention.email || null, // Email can be null initially
                department: intention.department,
                employment_type: intention.currentEmploymentType === 'ongoing' ? 'full_time' : 
                                intention.currentEmploymentType === 'fixed-term' ? 'contract' : 'part_time',
                fte: intention.targetFTE,
                status: 'active'
              };
              
              // Check if staff already exists
              const existingStaff = await staffService.getStaffByStaffId(intention.staffId);
              if (!existingStaff) {
                await staffService.createStaff(staffData);
                staffCreated++;
                console.log(`Created staff record for ${intention.staffName} (${intention.staffId})`);
              } else {
                // Deliberately does NOT write FTE here (issue #226).
                //
                // This branch used to just log and move on, which is why no
                // intention had ever updated an existing staff member's FTE — 47
                // of 193 staff were left measured against the wrong capacity.
                //
                // The fix is NOT to overwrite it silently on import. FTE is the
                // denominator of every utilisation figure and feeds load letters,
                // and it is not stored per year (#221), so applying a 2027 figure
                // also rewrites how 2026 reads. That belongs behind a review.
                //
                // FteReconciliationPanel on HR -> Intention Review surfaces every
                // mismatch and applies it on a deliberate click. Counting them
                // here just makes the import honest about what it did not do.
                if (!sameFte(existingStaff.fte, resolveIntendedFte(intention as any))) {
                  staffFteNeedsReview++;
                }
                console.log(`Staff record already exists for ${intention.staffName} (${intention.staffId})`);
              }
              
              if (!intention.email) {
                staffWithoutEmail++;
              }
            } catch (staffError) {
              console.warn('Could not create staff record for', intention.staffName, ':', staffError);
              // Continue with intention import even if staff creation fails
            }

            // Save the teaching intention
            await intentionsService.saveIntention(intention);
            processed++;
            setImportProgress(Math.round((processed / teachingIntentions.length) * 100));
          } catch (error) {
            console.error('Error saving intention for', intention.staffName, ':', error);
            // Continue with other records even if one fails
          }
        }

        // Provide detailed success message with next steps
        let message = `Successfully imported ${processed} teaching intentions`;
        if (staffCreated > 0) {
          message += ` and created ${staffCreated} staff records`;
        }
        if (staffWithoutEmail > 0) {
          message += `. Note: ${staffWithoutEmail} staff members need email addresses added in User Management for system access.`;
        }
        // Say out loud what the import did NOT do. Previously existing staff were
        // skipped in silence, so nobody knew their FTE had never been applied.
        if (staffFteNeedsReview > 0) {
          message += `. ${staffFteNeedsReview} existing staff requested a different FTE — review and apply these under HR → Intention Review.`;
        }
        
        addToast(message, 'success');
        
        // Reset state
        setImportInProgress(false);
        setActiveStep(1);
        setFiles([]);
        setMappings({});
        setValidationResults(null);
        setParsedCsvData([]);
        setCsvHeaders([]);
        
      } else {
        // Only the Teaching Intentions import currently writes to the database
        // (it also creates the staff records it references). Be honest for the
        // other types rather than faking a successful import.
        addToast('Importing this data type isn\'t available yet — only Teaching Intentions can be imported at the moment.', 'info');
        setImportInProgress(false);
      }
    } catch (error) {
      console.error('Import error:', error);
      addToast(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
      setImportInProgress(false);
    }
  };
  // Step 1: File Upload
  const UploadStep = () => <div className="space-y-6">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-ink-900 mb-2">
          Select Import Type
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button variant={importType === 'staff' ? 'primary' : 'secondary'} onClick={() => setImportType('staff')}>
            Staff Data
          </Button>
          <Button variant={importType === 'subjects' ? 'primary' : 'secondary'} onClick={() => setImportType('subjects')}>
            Subjects
          </Button>
          <Button variant={importType === 'classes' ? 'primary' : 'secondary'} onClick={() => setImportType('classes')}>
            Classes
          </Button>
          <Button variant={importType === 'teaching-intentions' ? 'primary' : 'secondary'} onClick={() => setImportType('teaching-intentions')}>
            Teaching Intentions
          </Button>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-medium text-ink-900 mb-2">
          Upload CSV File
        </h3>
        <FileUploader accept=".csv" onFilesSelected={handleFilesSelected} onError={error => addToast(error, 'error')} />
      </div>
      {csvParsing && <Card>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-600 mx-auto mb-4"></div>
            <p className="text-ink-500">Parsing CSV file...</p>
          </div>
        </Card>}
      {csvParseError && <Card>
          <div className="flex items-center p-4 bg-danger-bg border border-danger-border rounded-md">
            <XIcon className="h-5 w-5 text-danger-fg mr-3" />
            <div>
              <h4 className="text-sm font-medium text-danger-fg">CSV Parsing Error</h4>
              <p className="text-sm text-danger-fg mt-1">{csvParseError}</p>
            </div>
          </div>
        </Card>}
      {files.length > 0 && !csvParsing && !csvParseError && parsedCsvData.length > 0 && <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-ink-900">Data Preview ({parsedCsvData.length} rows)</h3>
            <div className="flex space-x-2">
              <Button variant="secondary" size="sm" onClick={() => {
                if (files.length > 0) {
                  handleFilesSelected(files);
                }
              }}>
                Re-parse File
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowDataPreview(!showDataPreview)}>
                {showDataPreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
            </div>
          </div>
          {showDataPreview && <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ink-200">
                <thead className="bg-ink-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Row
                    </th>
                    {csvHeaders.map(header => <th key={header} className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        {header}
                      </th>)}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-ink-200">
                  {parsedCsvData.slice(0, 5).map((row, rowIndex) => <tr key={rowIndex}>
                      <td className="font-mono tabular-nums px-3 py-2 whitespace-nowrap text-sm font-medium text-ink-900">
                        {rowIndex + 1}
                      </td>
                      {csvHeaders.map(header => <td key={`${rowIndex}-${header}`} className="px-3 py-2 whitespace-nowrap text-sm text-ink-500">
                          {row[header]}
                        </td>)}
                    </tr>)}
                </tbody>
              </table>
              {parsedCsvData.length > 5 && <div className="text-center text-sm text-ink-500 mt-2">
                  Showing 5 of {parsedCsvData.length} rows
                </div>}
            </div>}
        </Card>}
      <div className="border rounded-md p-4 bg-navy-50">
        <h4 className="text-sm font-medium text-navy-800 mb-2">
          Import Instructions
        </h4>
        <ul className="list-disc pl-5 text-sm text-navy-700 space-y-1">
          <li>Prepare your data in CSV format</li>
          <li>Ensure the first row contains column headers</li>
          <li>Make sure text data is properly quoted if it contains commas</li>
          <li>Dates should be in YYYY-MM-DD format</li>
          <li>
            Required fields for {importType} import:{' '}
            {getDbFields().filter(f => f.required).map(f => f.name.replace(/_/g, ' ')).join(', ')}
          </li>
          {importType === 'teaching-intentions' && (
            <>
              <li>Email addresses are optional for importing intentions</li>
              <li>Staff records will be created automatically during import</li>
              <li>Add email addresses later in Settings → User Management for system access</li>
              <li>Staff without emails can have intentions imported but cannot log in until emails are added</li>
              <li><strong>Multiple CSV columns can map to the same database field</strong> (e.g., ongoing vs fixed-term intention questions both map to "intention to return")</li>
            </>
          )}
        </ul>
      </div>
      <div className="flex justify-end">
        <Button variant="primary" rightIcon={<ArrowRightIcon className="h-4 w-4" />} onClick={nextStep} disabled={files.length === 0 || csvParsing || csvParseError || parsedCsvData.length === 0}>
          Next: Map Fields
        </Button>
      </div>
    </div>;
  // Step 2: Field Mapping
  const MappingStep = () => <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-ink-900">
            Map CSV Fields to Database Fields
          </h3>
          <p className="text-sm text-ink-500 mt-1">
            Match columns from your CSV file to the corresponding database
            fields
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="secondary" size="sm" onClick={() => setShowSaveMappingModal(true)}>
            Save Mapping
          </Button>
          <div className="relative">
            <select className="block w-full pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md" value={selectedMapping || ''} onChange={e => loadSavedMapping(e.target.value)}>
              <option value="">Load Saved Mapping</option>
              {savedMappings.filter(m => m.type === importType).map(mapping => <option key={mapping.id} value={mapping.id}>
                    {mapping.name} {mapping.isTemplate ? '(Template)' : ''}
                  </option>)}
            </select>
          </div>
        </div>
      </div>
      {/* Field Mapping Component */}
      <FieldMappingComponent csvHeaders={csvHeaders} dbFields={getDbFields()} mappings={mappings} setMappings={setMappings} transformations={transformations} setTransformations={setTransformations} openTransformModal={openTransformModal} parsedCsvData={parsedCsvData} />
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-ink-700">Mapping Preview</h4>
          <Button variant="secondary" size="sm" leftIcon={<EyeIcon className="h-4 w-4" />} onClick={() => addToast('Preview refreshed', 'success')}>
            Refresh Preview
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200">
            <thead className="bg-ink-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Row
                </th>
                {Array.from(new Set(Object.values(mappings).filter(dbField => dbField && dbField !== 'ignore'))).map((dbField, index) => <th key={`header-${dbField}-${index}`} className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      {dbField.replace(/_/g, ' ')}
                    </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {getMappedDataPreview().map((row, rowIndex) => <tr key={rowIndex}>
                  <td className="font-mono tabular-nums px-3 py-2 whitespace-nowrap text-sm font-medium text-ink-900">
                    {rowIndex + 1}
                  </td>
                  {Object.keys(row).map(field => <td key={`${rowIndex}-${field}`} className="px-3 py-2 whitespace-nowrap text-sm text-ink-500">
                      {Array.isArray(row[field]) ? row[field].join(', ') : typeof row[field] === 'boolean' ? row[field] ? 'Yes' : 'No' : row[field]}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center space-x-2">
          <div className="px-2 py-1 text-xs rounded-full bg-navy-100 text-navy-800">
            <DatabaseIcon className="h-3 w-3 inline mr-1" />
            Database Fields Mapped:{' '}
            {Object.values(mappings).filter(v => v && v !== 'ignore').length}{' '}
            / {getDbFields().length}
          </div>
          <div className="px-2 py-1 text-xs rounded-full bg-ok-bg text-ok-fg">
            <CheckIcon className="h-3 w-3 inline mr-1" />
            Required Fields Mapped:{' '}
            {getRequiredFields().filter(field => Object.values(mappings).includes(field)).length}{' '}
            / {getRequiredFields().length}
          </div>
          <div className="px-2 py-1 text-xs rounded-full bg-warn-bg text-warn-fg">
            <AlertTriangleIcon className="h-3 w-3 inline mr-1" />
            Fields with Transformations: {Object.keys(transformations).length}
          </div>
        </div>
      </Card>
      <div className="flex justify-between">
        <Button variant="secondary" leftIcon={<ArrowLeftIcon className="h-4 w-4" />} onClick={prevStep}>
          Back
        </Button>
        <Button variant="primary" rightIcon={<ArrowRightIcon className="h-4 w-4" />} onClick={nextStep} disabled={!areRequiredFieldsMapped()}>
          Next: Validate
        </Button>
      </div>
    </div>;
  // Step 3: Validation
  const ValidationStep = () => <div className="space-y-6">
      <h3 className="text-lg font-medium text-ink-900 mb-4">
        Validation Results
      </h3>
      {validationInProgress ? <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto"></div>
          <p className="mt-4 text-ink-500">Validating your data...</p>
        </div> : importInProgress ? <div className="space-y-4">
          <div className="text-center">
            <p className="text-lg font-medium text-ink-900">Importing Data</p>
            <p className="text-sm text-ink-500">
              Please wait while we import your data...
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-ink-700">
                Import progress
              </span>
              <span className="font-mono tabular-nums text-sm font-medium text-ink-900">
                {importProgress}%
              </span>
            </div>
            <div className="w-full bg-ink-200 rounded-full h-2.5">
              <div className="bg-navy-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" style={{
            width: `${importProgress}%`
          }}></div>
            </div>
          </div>
          <div className="border rounded-md p-4 bg-navy-50 text-navy-800 text-sm">
            <p>
              Processing row {Math.floor(importProgress / 100 * 50)} of 50
            </p>
          </div>
        </div> : <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="text-center p-4">
              <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">
                {validationResults.totalRows}
              </div>
              <div className="text-sm text-ink-500">Total Rows</div>
            </Card>
            <Card className="text-center p-4">
              <div className="font-mono tabular-nums text-2xl font-bold text-ok-fg">
                {validationResults.validRows}
              </div>
              <div className="text-sm text-ink-500">Valid Rows</div>
            </Card>
            <Card className="text-center p-4">
              <div className="font-mono tabular-nums text-2xl font-bold text-warn-fg">
                {validationResults.warnings}
              </div>
              <div className="text-sm text-ink-500">Warnings</div>
            </Card>
            <Card className="text-center p-4">
              <div className="font-mono tabular-nums text-2xl font-bold text-danger-fg">
                {validationResults.errors}
              </div>
              <div className="text-sm text-ink-500">Errors</div>
            </Card>
          </div>
          <div className="border rounded-md overflow-hidden">
            <div className="px-4 py-3 bg-ink-50 border-b flex justify-between items-center">
              <h4 className="text-sm font-medium text-ink-700">
                Validation Issues
              </h4>
              <div className="flex space-x-2">
                <Button variant="secondary" size="sm" leftIcon={<DownloadIcon className="h-4 w-4" />} onClick={() => addToast('Report downloaded', 'success')}>
                  Download Report
                </Button>
                <Button variant="secondary" size="sm" leftIcon={<FilterIcon className="h-4 w-4" />}>
                  Filter Issues
                </Button>
              </div>
            </div>
            <div className="divide-y divide-ink-200 max-h-[300px] overflow-y-auto">
              {validationResults.details.map((issue: any, index: number) => <div key={index} className="px-4 py-3">
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 ${issue.type === 'error' ? 'text-danger-fg' : 'text-warn-fg'}`}>
                      {issue.type === 'error' ? <XIcon className="h-5 w-5" /> : <AlertTriangleIcon className="h-5 w-5" />}
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex justify-between">
                        <p className="text-sm font-medium text-ink-900">
                          Row {issue.row}: {issue.message}
                        </p>
                        <span className="text-xs text-ink-500">
                          Field: {issue.field}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center">
                        <span className="text-xs text-ink-500 mr-2">
                          Value:
                        </span>
                        <code className="text-xs bg-ink-100 px-1 py-0.5 rounded">
                          {issue.value}
                        </code>
                      </div>
                      <div className="mt-2 flex justify-between items-center">
                        <p className="text-xs text-ink-700">
                          <span className="font-medium">Suggestion:</span>{' '}
                          {issue.suggestion}
                        </p>
                        <Button variant="secondary" size="sm" onClick={() => addToast('Fix applied', 'success')}>
                          Apply Fix
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="secondary" onClick={prevStep}>
              Back
            </Button>
            <div className="space-x-2">
              <Button variant="secondary" onClick={() => addToast('Download validation report feature coming soon', 'info')}>
                Download Report
              </Button>
              <Button variant="primary" onClick={importData} disabled={validationResults.errors > 0}>
                Import Data
              </Button>
            </div>
          </div>
        </>}
    </div>;
  // Render the current step
  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return <UploadStep />;
      case 2:
        return <MappingStep />;
      case 3:
        return <ValidationStep />;
      default:
        return <UploadStep />;
    }
  };
  return <div>
      <PageHeader title="Import Data" description="Import staff, subjects, and classes from CSV files" />
      <Card>
        <div className="mb-6">
          <div className="flex items-center">
            <div className="flex items-center relative">
              <div className={`flex items-center justify-center h-8 w-8 rounded-full ${activeStep >= 1 ? 'bg-navy-600 text-white' : 'bg-ink-200 text-ink-500'}`}>
                <UploadIcon className="h-4 w-4" />
              </div>
              <div className={`ml-2 text-sm font-medium ${activeStep === 1 ? 'text-navy-600' : 'text-ink-500'}`}>
                Upload
              </div>
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${activeStep >= 2 ? 'bg-navy-600' : 'bg-ink-200'}`}></div>
            <div className="flex items-center relative">
              <div className={`flex items-center justify-center h-8 w-8 rounded-full ${activeStep >= 2 ? 'bg-navy-600 text-white' : 'bg-ink-200 text-ink-500'}`}>
                <ArrowRightIcon className="h-4 w-4" />
              </div>
              <div className={`ml-2 text-sm font-medium ${activeStep === 2 ? 'text-navy-600' : 'text-ink-500'}`}>
                Map Fields
              </div>
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${activeStep >= 3 ? 'bg-navy-600' : 'bg-ink-200'}`}></div>
            <div className="flex items-center relative">
              <div className={`flex items-center justify-center h-8 w-8 rounded-full ${activeStep >= 3 ? 'bg-navy-600 text-white' : 'bg-ink-200 text-ink-500'}`}>
                <CheckIcon className="h-4 w-4" />
              </div>
              <div className={`ml-2 text-sm font-medium ${activeStep === 3 ? 'text-navy-600' : 'text-ink-500'}`}>
                Validate & Import
              </div>
            </div>
          </div>
        </div>
        {renderStep()}
      </Card>
      <Card className="mt-6">
        <Tabs tabs={[{
        id: 'templates',
        label: 'Import Templates',
        content: <div className="space-y-4 py-2">
                  <p className="text-sm text-ink-500">
                    Download CSV templates for importing different types of
                    data.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                      <h4 className="text-sm font-medium text-ink-900 mb-2">
                        Staff Template
                      </h4>
                      <p className="text-xs text-ink-500 mb-4">
                        Import staff members with their qualifications and
                        workload limits.
                      </p>
                      <Button variant="secondary" size="sm" onClick={() => addToast('Staff template downloaded', 'success')} fullWidth>
                        Download Template
                      </Button>
                    </Card>
                    <Card className="p-4">
                      <h4 className="text-sm font-medium text-ink-900 mb-2">
                        Subjects Template
                      </h4>
                      <p className="text-xs text-ink-500 mb-4">
                        Import subjects with requirements and class size limits.
                      </p>
                      <Button variant="secondary" size="sm" onClick={() => addToast('Subjects template downloaded', 'success')} fullWidth>
                        Download Template
                      </Button>
                    </Card>
                    <Card className="p-4">
                      <h4 className="text-sm font-medium text-ink-900 mb-2">
                        Classes Template
                      </h4>
                      <p className="text-xs text-ink-500 mb-4">
                        Import classes with student counts and room assignments.
                      </p>
                      <Button variant="secondary" size="sm" onClick={() => addToast('Classes template downloaded', 'success')} fullWidth>
                        Download Template
                      </Button>
                    </Card>
                  </div>
                </div>
      }, {
        id: 'history',
        label: 'Import History',
        content: <div className="border rounded-md p-10 text-center text-sm text-ink-500">
                  Import history isn't recorded yet. Completed imports will be listed here in a future update.
                </div>
      }, {
        id: 'settings',
        label: 'Import Settings',
        content: <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-ink-900 mb-2">
                      Default Import Behavior
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input id="update-existing" name="import-behavior" type="radio" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300" defaultChecked />
                        <label htmlFor="update-existing" className="ml-2 block text-sm text-ink-900">
                          Update existing records if found (match by email/ID)
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input id="skip-existing" name="import-behavior" type="radio" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300" />
                        <label htmlFor="skip-existing" className="ml-2 block text-sm text-ink-900">
                          Skip existing records (only add new)
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input id="create-duplicates" name="import-behavior" type="radio" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300" />
                        <label htmlFor="create-duplicates" className="ml-2 block text-sm text-ink-900">
                          Create duplicates (not recommended)
                        </label>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-ink-900 mb-2">
                      Validation Settings
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input id="strict-validation" name="validation-strictness" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" defaultChecked />
                        <label htmlFor="strict-validation" className="ml-2 block text-sm text-ink-900">
                          Strict validation (reject import if any errors)
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input id="warn-duplicates" name="warn-duplicates" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" defaultChecked />
                        <label htmlFor="warn-duplicates" className="ml-2 block text-sm text-ink-900">
                          Warn about potential duplicates
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="primary" onClick={() => addToast('Settings saved', 'success')}>
                      Save Settings
                    </Button>
                  </div>
                </div>
      }]} />
      </Card>
      {/* Save Mapping Modal */}
      {showSaveMappingModal && <div className="fixed inset-0 bg-ink-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-ink-900">
                Save Mapping Configuration
              </h3>
              <button className="text-ink-400 hover:text-ink-500" onClick={() => setShowSaveMappingModal(false)}>
                &times;
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-700 mb-1">
                Mapping Name
              </label>
              <input type="text" className="w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="E.g., Staff Import - Spring 2023" value={mappingName} onChange={e => setMappingName(e.target.value)} />
            </div>
            <div className="flex items-center mb-4">
              <input id="save-as-template" name="save-as-template" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" />
              <label htmlFor="save-as-template" className="ml-2 block text-sm text-ink-900">
                Save as template for future imports
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="secondary" onClick={() => setShowSaveMappingModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveCurrentMapping} disabled={!mappingName.trim()}>
                Save Mapping
              </Button>
            </div>
          </div>
        </div>}
      {/* Field Transformation Modal */}
      {showTransformModal && currentTransformField && <div className="fixed inset-0 bg-ink-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-ink-900">
                Transform Field: {currentTransformField}
              </h3>
              <button className="text-ink-400 hover:text-ink-500" onClick={() => setShowTransformModal(false)}>
                &times;
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-ink-500 mb-2">
                  Sample data:{' '}
                  <span className="font-medium">
                    {parsedCsvData[0]?.[currentTransformField]}
                  </span>
                </div>
                <div className="border-t border-b border-ink-200 py-4 space-y-3">
                  <div className="flex items-center">
                    <input id="trim-whitespace" name="trim-whitespace" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" defaultChecked={transformations[currentTransformField]?.trim} />
                    <label htmlFor="trim-whitespace" className="ml-2 block text-sm text-ink-900">
                      Trim whitespace
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Default value (if empty)
                    </label>
                    <input type="text" className="w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Leave blank for no default" defaultValue={transformations[currentTransformField]?.defaultValue || ''} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Split text into array using separator
                    </label>
                    <input type="text" className="w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="E.g., comma (,) or semicolon (;)" defaultValue={transformations[currentTransformField]?.splitOn || ''} />
                    <p className="mt-1 text-xs text-ink-500">
                      Leave blank to keep as single value
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input id="convert-number" name="convert-number" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" defaultChecked={transformations[currentTransformField]?.convertToNumber} />
                    <label htmlFor="convert-number" className="ml-2 block text-sm text-ink-900">
                      Convert to number
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input id="convert-boolean" name="convert-boolean" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" defaultChecked={transformations[currentTransformField]?.convertToBoolean} />
                    <label htmlFor="convert-boolean" className="ml-2 block text-sm text-ink-900">
                      Convert to boolean (true/false, yes/no, 1/0)
                    </label>
                  </div>
                </div>
              </div>
              <div className="bg-navy-50 p-3 rounded-md">
                <h4 className="text-sm font-medium text-navy-800 mb-2">
                  Transformation Preview
                </h4>
                <div className="space-y-2">
                  {parsedCsvData.slice(0, 3).map((row, idx) => {
                let value = row[currentTransformField];
                let transformedValue = value;
                // Apply sample transformations
                const trim = document.getElementById('trim-whitespace')?.checked;
                const defaultValue = (document.querySelector('input[placeholder="Leave blank for no default"]') as HTMLInputElement)?.value;
                const splitOn = (document.querySelector('input[placeholder="E.g., comma (,) or semicolon (;)"]') as HTMLInputElement)?.value;
                const convertToNumber = document.getElementById('convert-number')?.checked;
                const convertToBoolean = document.getElementById('convert-boolean')?.checked;
                if (trim && typeof value === 'string') {
                  transformedValue = value.trim();
                }
                if (defaultValue && (!value || value === '')) {
                  transformedValue = defaultValue;
                }
                if (splitOn && typeof value === 'string') {
                  transformedValue = value.split(splitOn).map(item => item.trim());
                }
                if (convertToNumber && !isNaN(Number(value))) {
                  transformedValue = Number(value);
                }
                if (convertToBoolean) {
                  transformedValue = ['true', 'yes', '1', 'y'].includes(String(value).toLowerCase());
                }
                return <div key={idx} className="flex items-center justify-between text-xs">
                        <div className="font-mono bg-white px-2 py-1 rounded border border-ink-200 overflow-x-auto max-w-[45%]">
                          {String(value)}
                        </div>
                        <ArrowRightIcon className="h-4 w-4 text-navy-500" />
                        <div className="font-mono bg-white px-2 py-1 rounded border border-navy-200 overflow-x-auto max-w-[45%]">
                          {Array.isArray(transformedValue) ? `[${transformedValue.join(', ')}]` : String(transformedValue)}
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <Button variant="secondary" onClick={() => setShowTransformModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => saveTransformation({
            trim: document.getElementById('trim-whitespace')?.checked,
            defaultValue: (document.querySelector('input[placeholder="Leave blank for no default"]') as HTMLInputElement)?.value,
            splitOn: (document.querySelector('input[placeholder="E.g., comma (,) or semicolon (;)"]') as HTMLInputElement)?.value,
            convertToNumber: document.getElementById('convert-number')?.checked,
            convertToBoolean: document.getElementById('convert-boolean')?.checked
          })}>
                Apply Transformation
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};
export default ImportData;